import type {
    CreateStockingCycleRequest,
    StockingCycleDto,
    StockingCycleListDto,
    UpdateStockingCycleRequest,
} from '@/services/types';
import { parseISOString } from '@/utils/date';

import type { ApiWire } from '../shared/types';

import type {
    CreateStockingCycleInput,
    StockingCycle,
    StockingCycleList,
    UpdateStockingCycleInput,
} from './types';

export function mapStockingCycleToDomain(dto: StockingCycleDto): StockingCycle {
    return {
        id: dto.id,
        locationId: dto.locationId,
        name: dto.name,
        status: dto.status,
        notes: dto.notes ?? null,
        plannedStartDate: dto.plannedStartDate ?? null,
        stockedFrom: parseISOString(dto.stockedFrom),
        stockedTo: parseISOString(dto.stockedTo),
        lastClosedAt: parseISOString(dto.lastClosedAt),
        fallowingUntil: parseISOString(dto.fallowingUntil),
        isFallowing: dto.isFallowing,
        groupCount: dto.groupCount,
        activeGroupCount: dto.activeGroupCount,
        createdAt: parseISOString(dto.createdAt) ?? new Date(0),
    };
}

export function mapStockingCycleListToDomain(dto: StockingCycleListDto): StockingCycleList {
    return {
        locationId: dto.locationId,
        cycles: (dto.cycles ?? []).map(mapStockingCycleToDomain),
        suggestedName: dto.suggestedName ?? '',
        unassignedGroupCount: dto.unassignedGroupCount ?? 0,
    };
}

export function mapCreateStockingCycleToRequest(
    input: CreateStockingCycleInput
): CreateStockingCycleRequest {
    const notes = input.notes?.trim();
    return {
        name: input.name.trim(),
        plannedStartDate: input.plannedStartDate,
        ...(notes ? { notes } : {}),
    };
}

/** The create endpoint's response is untyped in the spec, so read the id defensively. */
export function readCreatedCycleId(data: unknown): number | null {
    if (!data || typeof data !== 'object' || !('id' in data)) return null;
    const { id } = data;
    return typeof id === 'number' ? id : null;
}

/** Name and notes always, the planned date only when it changed; empty notes clear them. */
export function mapUpdateStockingCycleToRequest(
    input: UpdateStockingCycleInput
): ApiWire<UpdateStockingCycleRequest> {
    const notes = input.notes?.trim() ?? '';
    return {
        name: input.name.trim(),
        ...(input.plannedStartDate !== undefined
            ? { plannedStartDate: input.plannedStartDate }
            : {}),
        notes: notes === '' ? null : notes,
    };
}
